import type { ComposerMention, ReviewDocumentCardParams } from "~vue/api/generated/chat";
// Types correspond to vpoc/apps/prompts data models and vpoc/utils/protocol.py
import type { CollaborationAnalysisCard } from "~vue/api/generated/collaboration-analysis";
import type { UiTeamTag } from "~vue/api/generated/connections";
import type { DevelopmentGoal } from "~vue/api/generated/development-journeys";
import type { PerformanceReviewMemoryResult } from "~vue/api/generated/performance-review-memory";
import type { TeamPerspectiveChatCard } from "~vue/api/generated/team-perspective";
import type { ChatAction } from "~vue/chatActions";

import type { FeedbackTarget } from "./feedback";

// Chat entrance use case data - used for both regular chat entrance use cases and calendar coach use cases
export type ChatEntranceUseCaseData = {
    position?: number | null;
    label: string;
    chat_source: string;
    call_code: string;
    description: string;
    icon_url?: string | null;
    is_roleplay: boolean;
};

// --- Line types (discriminated union on `type`) --- //
// Source: vpoc/utils/protocol.py ParseActionsResult

export type ActionState = {
    dismissed: boolean;
    minimized: boolean;
    submitted: boolean;
};

export type FileUploadArgs = {
    name?: string | null;
    description?: string | null;
    content?: string | null;
    type?: string | null;
    size?: number | null;
    /** Backend-rejected file (e.g. over the size limit) — shown on the card but not counted toward the upload limit. */
    skipped?: boolean;
};

type ParseActionsContent = {
    type: "md";
    content: string;
    hidden: boolean;
};

type ParseActionsToolCall = {
    type: "tool";
    tool_call: Record<string, unknown>;
};

type BaseParseActionsAction = {
    type: "action";
    index: number;
    action_state: ActionState | null;
};

export type ParseActionsTaggedTeam = BaseParseActionsAction & {
    action_name: ChatAction.TAGGED_TEAM;
    action_params: UiTeamTag;
};

export type ParseActionsReviewDocumentCardAction = BaseParseActionsAction & {
    action_name: ChatAction.REVIEW_DOCUMENT_UPLOAD;
    action_params: ReviewDocumentCardParams;
};

export type ParseActionsFileUploadAction = BaseParseActionsAction & {
    action_name: ChatAction.FILE_UPLOAD;
    action_params: FileUploadArgs[];
};

export type ParseActionsTaggedPerson = BaseParseActionsAction & {
    action_name: ChatAction.TAGGED_PERSON;
    action_params: {
        name: string;
        title: string | null;
        connection: string; // UUID
        match_text: string;
        was_created: boolean;
        relationship: string | null;
        is_first_connection_for_user: boolean;
        // Optional: absent on messages persisted before profile enrichment was added.
        profile_pulled?: boolean;
        has_notes?: boolean;
        has_details?: boolean;
        collaboration_profile_shared?: boolean;
    };
};

export type ParseActionsInferredProfileAnswer = BaseParseActionsAction & {
    action_name: ChatAction.INFERRED_PROFILE_ANSWER;
    action_params: {
        key: string;
        heading: string;
        question: string | null;
        inferred_answer: string;
        user_profile_data_id: string;
    };
};

export type ParseActionsCustomizableTextAction = BaseParseActionsAction & {
    action_name: ChatAction.CUSTOMIZABLE_TEXT;
    action_params: {
        title: string | null;
        content: string | null;
        link: string | null;
        link_text: string | null;
    };
};

export type ParseActionsValuesInsightAction = BaseParseActionsAction & {
    action_name: ChatAction.VALUES_INSIGHT;
    action_params: {
        final_response: string | null;
        short_title: string | null;
    };
};

export type ParseActionsInternalThinkingAction = BaseParseActionsAction & {
    action_name: ChatAction.INTERNAL_THINKING;
    action_params: {
        final_response: string | null;
        short_title: string | null;
    };
};

export type ParseActionsFollowUpAction = BaseParseActionsAction & {
    action_name: ChatAction.FOLLOW_UP;
    action_params: {
        calendar_event_id: number;
        event_at_guessed: string | null;
        event_at_confirmed: string | null;
        title: string;
        chat_message_id: number | null;
        invite_description: string | null;
        recurring: boolean;
        custom_link: string | null;
    };
};

export type ParseActionsActionItemsAction = BaseParseActionsAction & {
    action_name: ChatAction.ACTION_ITEMS;
    action_params: {
        action_items: Array<{ content: string; checked: boolean }>;
        email_summary?: string | null;
        email_sent?: boolean | null;
    };
};

export type ParseActionsRolePlaySetupAction = BaseParseActionsAction & {
    action_name: ChatAction.ROLE_PLAY_SETUP;
    action_params: {
        name: string | null;
        relationship: string | null;
        personality: string | null;
        context: string | null;
    };
};

export enum RolePlayAdjustmentEnum {
    MORE_DEFENSIVE = "MORE_DEFENSIVE",
    MORE_ARGUMENTATIVE = "MORE_ARGUMENTATIVE",
    MORE_DIRECT = "MORE_DIRECT",
    IS_OK = "IS_OK",
}

export type RolePlayAdjustment = `${RolePlayAdjustmentEnum}`; // string literal type derived from enum values

// used from ChatDetail and prop drilled down, sourced from RolePlayAdjustment.choices() in backend
export type RolePlayAdjustmentProp = [RolePlayAdjustment, string][];

export type ParseActionsRolePlayAdjustAction = BaseParseActionsAction & {
    action_name: ChatAction.ROLE_PLAY_ADJUST;
    action_params: {
        adjustment: RolePlayAdjustment | null;
        adjustments: Record<RolePlayAdjustment, string>;
    };
};

export type ParseActionsRolePlayMonitorAction = BaseParseActionsAction & {
    action_name: ChatAction.ROLE_PLAY_MONITOR;
    action_params: {
        feedback: string;
        sentiment: "praise" | "suggestion" | "none";
        phase: "before" | "during" | "after" | "pause";
        is_significant: boolean;
        role_play_hashid: string | null;
    };
};

export type ParseActionsRolePlaySummaryAction = BaseParseActionsAction & {
    action_name: ChatAction.ROLE_PLAY_SUMMARY;
    action_params: {
        role_play_hashid: string;
    };
};

export type DevelopmentGoalDraft = {
    title: string;
    achievement_criteria: string;
    target_date: string | null;
};

export type ParseActionsSetUpDevelopmentGoalAction = BaseParseActionsAction & {
    action_name: ChatAction.SET_UP_DEVELOPMENT_GOAL;
    action_params: {
        development_goal: DevelopmentGoalDraft;
        lock_in_explanation?: string;
    };
};

export type ParseActionsDevelopmentGoalLockedInAction = BaseParseActionsAction & {
    action_name: ChatAction.DEVELOPMENT_GOAL_LOCKED_IN;
    action_params: {
        development_goal: DevelopmentGoalDraft;
        source_action_index: number;
    };
};

export type DevelopmentGoalUpdateProposal = {
    goal_id: string;
    title: string;
    achievement_criteria: string;
    current_target_date: string | null;
    change: "achieved" | "no_longer_pursuing" | "target_date";
    target_date: string | null;
};

// Mirrors ProposeDevelopmentGoalUpdateTool's result and the confirmed state recorded by its chat action.
export type ParseActionsProposeDevelopmentGoalUpdateAction = BaseParseActionsAction & {
    action_name: ChatAction.PROPOSE_DEVELOPMENT_GOAL_UPDATE;
    action_params: {
        goal_update?: DevelopmentGoalUpdateProposal;
        saved_goal?: Pick<DevelopmentGoal, "status" | "target_date">;
        error?: string;
    };
};

export type ParseActionsProactiveCoachingOfferAction = BaseParseActionsAction & {
    action_name: ChatAction.PROACTIVE_COACHING_OFFER;
    action_params: {
        status: "offer" | "fallback_thin" | "fallback_guardrail";
        card_header: string;
        card_subtext: string;
        handoff_context: string;
        modality: "make_a_plan" | "practice_together" | "draft_it" | "structure_the_conversation" | "break_it_down" | "reflect" | null;
        moment_id: number;
        cta_id: number;
        feedback?: "up" | "down" | null;
        saved_at?: string | null;
        unsaved_at?: string | null;
    };
};

export type ParseActionsProactiveFeatureOfferAction = BaseParseActionsAction & {
    action_name: ChatAction.PROACTIVE_FEATURE_OFFER;
    action_params: {
        card_header: string;
        card_subtext: string;
        feature: "calendar" | "my_work_insights" | "perspectives" | "collaboration_profile" | "journeys" | "microsoft_teams" | "slack";
        moment_id: number;
        cta_id: number;
        feedback?: "up" | "down" | null;
        saved_at?: string | null;
        unsaved_at?: string | null;
    };
};

export type ParseActionsPushManagerReviewToWorkdayAction = BaseParseActionsAction & {
    action_name: ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY;
    action_params: {
        draft?: {
            review_ref: string;
            overall_comment: string;
            sections: Array<{
                section_id: string;
                label: string;
                comment: string;
            }>;
        };
        reviewee_name?: string | null;
        integration_name?: string | null;
        draft_version?: number;
    };
};

// Mirrors EmailDraftActionParams / EmailDraftVariant in coach_core/coach_core/chat/email_drafts.py,
// which is the source of truth for this shape. Keep the two in step.
export type EmailDraftVariant = {
    label: string | null;
    subject: string;
    body: string;
};

export type ParseActionsEmailDraftAction = BaseParseActionsAction & {
    action_name: ChatAction.EMAIL_DRAFT;
    action_params: {
        variants: EmailDraftVariant[];
    };
};

// Django chat-tool result: llm/chat_tools/get_assessment_results.py.
export type ParseActionsAssessmentResultsAction = BaseParseActionsAction & {
    action_name: ChatAction.GET_ASSESSMENT_RESULTS;
    action_params: {
        status: "available" | "found" | "not_found" | "disabled";
        assessments: Array<{
            cycle_id: string;
            name: string;
            kind: "self_assessment" | "360";
            generated_at: string | null;
            results_path: string;
        }>;
        has_more: boolean;
        report_context: string;
        tool_call_id: string;
        tool_call_completed: boolean;
    };
};

// Django chat-tool result: llm/chat_tools/recent_document_uploads.py.
export type ParseActionsRecentDocumentsAction = BaseParseActionsAction & {
    action_name: ChatAction.QUERY_RECENT_DOCUMENT_UPLOADS;
    action_params: {
        found: boolean;
        extract: string;
        matched_file_names: string[];
        searched_file_names?: string[];
        // Older persisted tool calls contain only filenames.
        matched_documents?: Array<{ name: string; uploaded_at: string }>;
        tool_call_id: string;
        tool_call_completed: boolean;
    };
};

export type ParseActionsPastPerformanceReviewsAction = BaseParseActionsAction & {
    action_name: ChatAction.QUERY_PAST_PERFORMANCE_REVIEWS;
    action_params: Pick<PerformanceReviewMemoryResult, "status" | "subject" | "person_name"> & {
        tool_call_id: string;
        tool_call_completed: boolean;
    };
};

export type ParseActionsTeamPerspectiveAction = BaseParseActionsAction & {
    action_name: ChatAction.GET_TEAM_PERSPECTIVE;
    action_params: TeamPerspectiveChatCard & { tool_call_id: string; tool_call_completed: boolean };
};

// Compact, body-free result of the Django artifact chat tools (llm/chat_tools/artifact_tools.py).
// Bodies are fetched by the side pane from /v1/artifacts, never carried in the transcript.
export type ArtifactSummary = {
    artifact_id: string;
    title: string;
    kind: string;
    status: string;
    path: string;
    version_number: number;
    change_summary?: string;
    updated_at?: string;
};

export type ParseActionsArtifactAction = BaseParseActionsAction & {
    action_name:
        | ChatAction.CREATE_ARTIFACT
        | ChatAction.READ_ARTIFACT
        | ChatAction.EDIT_ARTIFACT
        | ChatAction.REWRITE_ARTIFACT
        | ChatAction.LIST_ARTIFACTS
        | ChatAction.SHOW_ARTIFACT;
    action_params: {
        tool_call_id: string;
        tool_call_completed: boolean;
        artifact?: ArtifactSummary;
        artifacts?: ArtifactSummary[];
        artifact_id?: string;
        count?: number;
        edits_applied?: number;
        already_saved_this_turn?: boolean;
        already_shown?: { artifact_id: string; title: string };
        list_limit_reached?: boolean;
        list_lookup_failed?: boolean;
        unfiltered_total?: number;
        version_number?: number;
        is_current?: boolean;
        filtered?: boolean;
        offset?: number;
        total?: number;
        next_offset?: number | null;
        error?: string;
    };
};

export type ParseActionsComposerMentions = BaseParseActionsAction & { action_name: ChatAction.COMPOSER_MENTIONS; action_params: { mentions: ComposerMention[] } };

export type ParseActionsGenericAction = BaseParseActionsAction & {
    action_name: Exclude<
        ChatAction,
        | ChatAction.COMPOSER_MENTIONS
        | ChatAction.FILE_UPLOAD
        | ChatAction.PUSH_MANAGER_REVIEW_TO_WORKDAY
        | ChatAction.TAGGED_PERSON
        | ChatAction.TAGGED_TEAM
        | ChatAction.CUSTOMIZABLE_TEXT
        | ChatAction.VALUES_INSIGHT
        | ChatAction.INTERNAL_THINKING
        | ChatAction.FOLLOW_UP
        | ChatAction.ACTION_ITEMS
        | ChatAction.ROLE_PLAY_SETUP
        | ChatAction.ROLE_PLAY_ADJUST
        | ChatAction.ROLE_PLAY_MONITOR
        | ChatAction.ROLE_PLAY_SUMMARY
        | ChatAction.INFERRED_PROFILE_ANSWER
        | ChatAction.WORKIQ_COLLABORATION_SOURCES
        | ChatAction.PROACTIVE_COACHING_OFFER
        | ChatAction.PROACTIVE_FEATURE_OFFER
        | ChatAction.SET_UP_DEVELOPMENT_GOAL
        | ChatAction.DEVELOPMENT_GOAL_LOCKED_IN
        | ChatAction.PROPOSE_DEVELOPMENT_GOAL_UPDATE
        | ChatAction.EMAIL_DRAFT
        | ChatAction.GET_ASSESSMENT_RESULTS
        | ChatAction.GET_TEAM_PERSPECTIVE
        | ChatAction.EXPLORE_COLLABORATION
        | ChatAction.REVIEW_DOCUMENT_UPLOAD
        | ChatAction.QUERY_RECENT_DOCUMENT_UPLOADS
        | ChatAction.CREATE_ARTIFACT
        | ChatAction.READ_ARTIFACT
        | ChatAction.EDIT_ARTIFACT
        | ChatAction.REWRITE_ARTIFACT
        | ChatAction.LIST_ARTIFACTS
    >;
    action_params: Record<string, unknown>;
};

export type ParseActionsCollaborationAnalysis = BaseParseActionsAction & {
    action_name: ChatAction.EXPLORE_COLLABORATION;
    action_params: { card: CollaborationAnalysisCard; tool_call_id: string; tool_call_completed: boolean };
};

export type CollaborationCitation = {
    title: string;
    url: string;
    source_type: string;
    detail: string;
};

export type ParseActionsCollaborationSources = BaseParseActionsAction & {
    action_name: ChatAction.WORKIQ_COLLABORATION_SOURCES;
    action_params: {
        sources: CollaborationCitation[];
    };
};

type ParseActionsAction =
    | ParseActionsComposerMentions
    | ParseActionsReviewDocumentCardAction
    | ParseActionsFileUploadAction
    | ParseActionsTaggedPerson
    | ParseActionsTaggedTeam
    | ParseActionsCustomizableTextAction
    | ParseActionsValuesInsightAction
    | ParseActionsInternalThinkingAction
    | ParseActionsFollowUpAction
    | ParseActionsActionItemsAction
    | ParseActionsRolePlaySetupAction
    | ParseActionsRolePlayAdjustAction
    | ParseActionsRolePlayMonitorAction
    | ParseActionsRolePlaySummaryAction
    | ParseActionsInferredProfileAnswer
    | ParseActionsCollaborationSources
    | ParseActionsProactiveCoachingOfferAction
    | ParseActionsProactiveFeatureOfferAction
    | ParseActionsSetUpDevelopmentGoalAction
    | ParseActionsDevelopmentGoalLockedInAction
    | ParseActionsProposeDevelopmentGoalUpdateAction
    | ParseActionsPushManagerReviewToWorkdayAction
    | ParseActionsEmailDraftAction
    | ParseActionsAssessmentResultsAction
    | ParseActionsTeamPerspectiveAction
    | ParseActionsCollaborationAnalysis
    | ParseActionsRecentDocumentsAction
    | ParseActionsPastPerformanceReviewsAction
    | ParseActionsArtifactAction
    | ParseActionsGenericAction;

export type ChatMessageLine = ParseActionsAction | ParseActionsContent | ParseActionsToolCall;

/** Extract specific action type(s) from the ParseActionsAction union by action_name discriminant */
export type ActionLineForName<T extends ChatAction> = Extract<ParseActionsAction, { action_name: T }>;

// --- ChatMessage --- //
// Source: vpoc/utils/protocol.py ChatMessageLines (serialized via model_dump in InitializeChatHandler)

export type ChatMessage = {
    // Fields from backend (ChatMessageLines)
    id: number;
    chat_message_id: number;
    role: string;
    dictated: boolean;
    lines: ChatMessageLine[];
    created_at: string;
    phase: string | null;
    feedback_target: FeedbackTarget | null;

    // Frontend runtime properties (set during streaming/interaction in ChatDetail.vue)
    streaming_content?: string;
    hidden?: boolean;
    message_id?: number;
    temp_id?: string;
    chat_message_uuid?: string;
    content?: string;
    files?: FileUploadArgs[];
    uce_id?: string;
};

export type InChatRolePlayData = {
    name: string;
    relationship: string;
    personality: string;
    context: string;
    chatType: "realtime" | "text";
};

export type UpdateActionState = (action: Partial<ParseActionsAction> & { lineIdx: number; message_id: number }) => void;

export type FocusableActionParams = {
    messageId: string | number;
    actionName: ChatAction;
    lineIdx: number;
    onFocus: () => void;
};

export type FocusWidgetParams = {
    actionName: ChatAction;
    messageId?: string | number;
    lineIdx?: number;
};
